import { u2000AggregateTimesheetBankHours } from "./U2000TimesheetBankAggregates";

const HONORARIA_DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;

function toFiniteNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

export type U2000TimesheetDerivedTotalsPatch = {
  totalPaidHours: number;
  totalLeaveHours: number;
  totalSpecialWithdraw: number;
  totalSpecialDeposit: number;
  totalSpecialNet: number;
  totalExpenses: number;
  totalEquipment: number;
  approvalPaidHours: number;
};

/** Données minimales pour recalculer les totaux en-tête (U3129 / U3130). */
export type U2000TimesheetDerivedTotalsSource = {
  honoraria?: ReadonlyArray<Partial<Record<(typeof HONORARIA_DAYS)[number], unknown>>>;
  bankHoursList?: readonly unknown[];
  expenses?: ReadonlyArray<{ amount?: unknown }>;
  equipment?: ReadonlyArray<{ amount?: unknown }>;
  status?: string;
  totalPaidHours?: unknown;
  approvalPaidHours?: unknown;
};

/**
 * Totaux projet / absences-banques / dépenses / équipement alignés sur les lignes du formulaire.
 * `totalLeaveHours` = congés uniquement (sans banque accumulée retrait/dépôt).
 */
export function u2000ComputeTimesheetDerivedTotals(source: U2000TimesheetDerivedTotalsSource): U2000TimesheetDerivedTotalsPatch {
  const nextProjectHours = (source.honoraria ?? []).reduce((sum, row) => {
    return (
      sum +
      HONORARIA_DAYS.reduce((rowSum, day) => {
        return rowSum + toFiniteNumber(row?.[day]);
      }, 0)
    );
  }, 0);

  const bankAgg = u2000AggregateTimesheetBankHours(source.bankHoursList as Record<string, unknown>[]);
  const nextTotalExpenses = (source.expenses ?? []).reduce((sum, row) => sum + toFiniteNumber(row?.amount), 0);
  const nextTotalEquipment = (source.equipment ?? []).reduce((sum, row) => sum + toFiniteNumber(row?.amount), 0);

  const isOpenOrWaiting = source.status === "Open" || source.status === "WaitingForApproval";
  const paidSnapshot =
    typeof source.approvalPaidHours === "number" && Number.isFinite(source.approvalPaidHours)
      ? source.approvalPaidHours
      : !isOpenOrWaiting
        ? toFiniteNumber(source.totalPaidHours)
        : 0;

  return {
    totalPaidHours: nextProjectHours,
    totalLeaveHours: bankAgg.absenceHours,
    totalSpecialWithdraw: bankAgg.specialWithdraw,
    totalSpecialDeposit: bankAgg.specialDeposit,
    totalSpecialNet: bankAgg.specialNet,
    totalExpenses: nextTotalExpenses,
    totalEquipment: nextTotalEquipment,
    approvalPaidHours: paidSnapshot,
  };
}

export function u2000MergeTimesheetDerivedTotalsInto<T extends U2000TimesheetDerivedTotalsSource>(data: T): T & U2000TimesheetDerivedTotalsPatch {
  return { ...data, ...u2000ComputeTimesheetDerivedTotals(data) };
}

/** Total honoraires payés = projet + absences + (retrait − dépôt). */
export function u2000TimesheetCombinedHonorariumHours(totals: Pick<U2000TimesheetDerivedTotalsPatch, "totalPaidHours" | "totalLeaveHours" | "totalSpecialNet">): number {
  return totals.totalPaidHours + totals.totalLeaveHours + totals.totalSpecialNet;
}

/** Total travaillé feuille (dialogue approbation, sommaire) recalculé depuis les lignes. */
export function u2000TimesheetTotalWorkedHours(source: U2000TimesheetDerivedTotalsSource): number {
  const derived = u2000ComputeTimesheetDerivedTotals(source);
  return Math.round(u2000TimesheetCombinedHonorariumHours(derived) * 100) / 100;
}
